import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Calendar, CheckCircle2, Clock, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AppModal } from '../../../components/ui/app-modal';
import { Button } from '../../../components/ui/button';
import { usePermissions } from '../../../hooks/use-permissions';
import { api } from '../../../lib/ky';
import { cn } from '../../../lib/utils';

// ── Types ──────────────────────────────────────────────────────────────────────

type ExtStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

interface ExtensionRequest {
  id: string;
  status: ExtStatus;
  daysRequested: number;
  daysGranted: number | null;
  reason: string | null;
  oldSubmissionDate: string | null;
  newSubmissionDate: string | null;
  requestedDate: string | null;
  approvalComments: string | null;
  rejectionReason: string | null;
  approvedAt: string | null;
  createdAt: string;
  createdBy: { id: string; name: string };
  approvedBy: { id: string; name: string } | null;
}

// ── Config ─────────────────────────────────────────────────────────────────────

const STATUS_CFG: Record<ExtStatus, { label: string; className: string; dot: string }> = {
  PENDING: {
    label: 'Pending',
    className: 'bg-warning-50 text-warning-600 border-warning-200',
    dot: 'bg-warning-400',
  },
  APPROVED: {
    label: 'Approved',
    className: 'bg-success-50 text-success-600 border-success-200',
    dot: 'bg-success-500',
  },
  REJECTED: {
    label: 'Rejected',
    className: 'bg-error-50 text-error-600 border-error-200',
    dot: 'bg-error-500',
  },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmt(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(iso));
}

function StatusBadge({ status }: { status: ExtStatus }) {
  const cfg = STATUS_CFG[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold',
        cfg.className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full shrink-0', cfg.dot)} />
      {cfg.label}
    </span>
  );
}

// ── Request Extension Modal ────────────────────────────────────────────────────

function RequestExtensionModal({
  open,
  onClose,
  onSubmit,
  isPending,
  effectiveDate,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (days: number, reason: string) => void;
  isPending: boolean;
  effectiveDate: string | null;
}) {
  const [days, setDays] = useState('');
  const [reason, setReason] = useState('');

  function handleClose() {
    setDays('');
    setReason('');
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const d = Number.parseInt(days, 10);
    if (!d || d < 1) return;
    onSubmit(d, reason);
  }

  const daysNum = Number.parseInt(days, 10);
  const isValid = !Number.isNaN(daysNum) && daysNum >= 1;

  const newSubmissionDate =
    isValid && effectiveDate
      ? (() => {
          const d = new Date(effectiveDate);
          d.setDate(d.getDate() + daysNum);
          return d.toISOString();
        })()
      : null;

  return (
    <AppModal open={open} onClose={handleClose} title="Request Extension">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-700">
            Number of Days <span className="text-error-500">*</span>
          </label>
          <input
            type="number"
            min={1}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            placeholder="e.g. 30"
            required
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
          <p className="mt-1 text-xs text-gray-400">
            Minimum 1 day. The new submission date will be calculated automatically.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
              Current Submission Date
            </p>
            <p className="mt-0.5 text-sm font-semibold text-gray-800">
              {effectiveDate ? fmt(effectiveDate) : '—'}
            </p>
          </div>
          <div>
            <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
              New Submission Date
            </p>
            <p className="mt-0.5 text-sm font-semibold text-brand-600">
              {newSubmissionDate ? fmt(newSubmissionDate) : '—'}
            </p>
          </div>
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-700">
            Reason
            <span className="ml-1 text-gray-400">(optional)</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Explain why you need more time…"
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={isPending || !isValid}>
            {isPending ? 'Submitting…' : 'Submit Request'}
          </Button>
        </div>
      </form>
    </AppModal>
  );
}

// ── Approve / Modify & Approve Modal ──────────────────────────────────────────

function ApproveModal({
  open,
  onClose,
  onSubmit,
  isPending,
  defaultDays,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (daysGranted: number, comments: string) => void;
  isPending: boolean;
  defaultDays: number;
}) {
  const [days, setDays] = useState(String(defaultDays));
  const [comments, setComments] = useState('');

  function handleClose() {
    setDays(String(defaultDays));
    setComments('');
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const d = Number.parseInt(days, 10);
    if (!d || d < 1) return;
    onSubmit(d, comments);
  }

  const daysNum = Number.parseInt(days, 10);
  const isModified = daysNum !== defaultDays;
  const isValid = !Number.isNaN(daysNum) && daysNum >= 1;

  return (
    <AppModal open={open} onClose={handleClose} title="Approve Extension Request">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-600">
          Adjust the days to grant if needed, then confirm approval.
        </p>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-700">
            Days to Grant <span className="text-error-500">*</span>
          </label>
          <input
            type="number"
            min={1}
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
          {isModified && (
            <p className="mt-1 text-xs text-warning-600">
              Modified from requested {defaultDays} days
            </p>
          )}
        </div>
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-700">
            Approval Comments
            <span className="ml-1 text-gray-400">(optional)</span>
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={2}
            placeholder="Optional notes for the record…"
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={isPending || !isValid}
            className="bg-success-500 hover:bg-success-600 focus:ring-success-200"
          >
            <CheckCircle2 size={13} className="mr-1" />
            {isPending ? 'Approving…' : isModified ? 'Modify & Approve' : 'Approve'}
          </Button>
        </div>
      </form>
    </AppModal>
  );
}

// ── Reject Modal ───────────────────────────────────────────────────────────────

function RejectExtModal({
  open,
  onClose,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (comments: string) => void;
  isPending: boolean;
}) {
  const [comments, setComments] = useState('');

  function handleClose() {
    setComments('');
    onClose();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!comments.trim()) return;
    onSubmit(comments.trim());
  }

  return (
    <AppModal open={open} onClose={handleClose} title="Reject Extension Request">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-xs font-medium text-gray-700">
            Rejection Reason <span className="text-error-500">*</span>
          </label>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            required
            placeholder="Explain the reason for rejecting this extension request…"
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
          <p className="mt-1 text-xs text-gray-400">
            {comments.trim().length === 0
              ? 'Comments are required'
              : `${comments.trim().length} characters`}
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={isPending || !comments.trim()}
            className="bg-error-500 hover:bg-error-600 focus:ring-error-200"
          >
            {isPending ? 'Rejecting…' : 'Reject Request'}
          </Button>
        </div>
      </form>
    </AppModal>
  );
}

// ── Extension Request Row ──────────────────────────────────────────────────────

function ExtensionRow({
  ext,
  studyRequestId,
  canDecide,
  onMutated,
}: {
  ext: ExtensionRequest;
  studyRequestId: string;
  canDecide: boolean;
  onMutated: () => void;
}) {
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const base = `study-requests/${studyRequestId}/extension-requests/${ext.id}`;

  const approveMutation = useMutation({
    mutationFn: ({ daysGranted, comments }: { daysGranted: number; comments: string }) =>
      api
        .post(`${base}/approve`, {
          json: { daysGranted, comments: comments || undefined },
        })
        .json(),
    onSuccess: () => {
      setApproveOpen(false);
      onMutated();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (comments: string) => api.post(`${base}/reject`, { json: { comments } }).json(),
    onSuccess: () => {
      setRejectOpen(false);
      onMutated();
    },
  });

  const isPending = ext.status === 'PENDING';

  return (
    <tr className="border-b border-gray-100 transition-colors hover:bg-gray-50/50 last:border-0">
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Clock size={12} className="shrink-0 text-gray-400" />
          {fmt(ext.createdAt)}
        </div>
        {ext.reason && (
          <p className="mt-0.5 text-xs text-gray-400 italic truncate max-w-[200px]">{ext.reason}</p>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <Calendar size={12} className="shrink-0 text-gray-400" />
          {fmt(ext.oldSubmissionDate)}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-1.5 text-xs text-gray-600">
          <Calendar size={12} className="shrink-0 text-gray-400" />
          {fmt(ext.newSubmissionDate ?? ext.requestedDate)}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="text-center">
          <span className="rounded-lg bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-700">
            {ext.status === 'APPROVED' && ext.daysGranted != null
              ? ext.daysGranted
              : ext.daysRequested}
            d
          </span>
          {ext.status === 'APPROVED' &&
            ext.daysGranted != null &&
            ext.daysGranted !== ext.daysRequested && (
              <p className="mt-0.5 text-[10px] text-gray-400">(req. {ext.daysRequested}d)</p>
            )}
        </div>
      </td>
      <td className="px-4 py-3">
        <StatusBadge status={ext.status} />
        {ext.status === 'APPROVED' && ext.approvedBy && (
          <p className="mt-0.5 text-[10px] text-gray-400">by {ext.approvedBy.name}</p>
        )}
        {ext.status === 'REJECTED' && ext.rejectionReason && (
          <p className="mt-0.5 text-[10px] text-error-400 truncate max-w-[160px]">
            {ext.rejectionReason}
          </p>
        )}
      </td>
      <td className="px-4 py-3">
        {canDecide && isPending ? (
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              onClick={() => setApproveOpen(true)}
              className="bg-success-500 hover:bg-success-600 focus:ring-success-200 text-xs px-2.5 py-1"
            >
              <CheckCircle2 size={12} className="mr-1" />
              Approve
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setRejectOpen(true)}
              className="border-error-200 text-error-600 hover:bg-error-50 text-xs px-2.5 py-1"
            >
              <X size={12} className="mr-1" />
              Reject
            </Button>
          </div>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </td>

      <ApproveModal
        open={approveOpen}
        onClose={() => setApproveOpen(false)}
        onSubmit={(daysGranted, comments) => approveMutation.mutate({ daysGranted, comments })}
        isPending={approveMutation.isPending}
        defaultDays={ext.daysRequested}
      />
      <RejectExtModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onSubmit={(comments) => rejectMutation.mutate(comments)}
        isPending={rejectMutation.isPending}
      />
    </tr>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function ExtensionRequestsTab({
  requestId,
  reportSubmissionDate,
  onMutated,
  autoOpenTrigger,
}: {
  requestId: string;
  reportSubmissionDate: string | null;
  onMutated: () => void;
  autoOpenTrigger?: number;
}) {
  const { can } = usePermissions();
  const canCreate = can('sm-extension:create');
  const canDecide = can('sm-extension:decide');
  const [createOpen, setCreateOpen] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    if (autoOpenTrigger) setCreateOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenTrigger]);

  const extKey = ['extension-requests', requestId];

  const {
    data: extensions,
    isLoading,
    isError,
  } = useQuery({
    queryKey: extKey,
    queryFn: () =>
      api.get(`study-requests/${requestId}/extension-requests`).json<ExtensionRequest[]>(),
  });

  const createMutation = useMutation({
    mutationFn: ({ days, reason }: { days: number; reason: string }) =>
      api
        .post(`study-requests/${requestId}/extension-requests`, {
          json: { daysRequested: days, reason: reason || undefined },
        })
        .json(),
    onSuccess: () => {
      setCreateOpen(false);
      qc.invalidateQueries({ queryKey: extKey });
      onMutated();
    },
  });

  const effectiveDate = (() => {
    const approved = (extensions ?? [])
      .filter((e) => e.status === 'APPROVED' && e.newSubmissionDate)
      .sort((a, b) => new Date(b.approvedAt!).getTime() - new Date(a.approvedAt!).getTime());
    return approved.length > 0 ? approved[0].newSubmissionDate : reportSubmissionDate;
  })();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800">Extension Requests</h3>
        <p className="text-xs text-gray-400">
          Deadline extension requests for the study report submission.
        </p>
      </div>

      {/* Current Submission Date banner */}
      <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm">
        <Calendar size={15} className="shrink-0 text-brand-500" />
        <div>
          <p className="text-xs font-medium text-gray-500">Current Report Submission Date</p>
          <p className="text-sm font-semibold text-gray-800">{fmt(effectiveDate)}</p>
        </div>
        {effectiveDate && effectiveDate !== reportSubmissionDate && (
          <span className="ml-auto rounded-full bg-success-50 border border-success-200 px-2.5 py-0.5 text-xs font-semibold text-success-600">
            Extended
          </span>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-12 animate-pulse rounded-xl bg-gray-100" />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && (
        <div className="rounded-2xl border border-error-200 bg-error-50 p-6 text-center">
          <p className="text-sm font-medium text-error-600">Failed to load extension requests</p>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !isError && (extensions ?? []).length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center shadow-sm">
          <Clock size={32} className="mx-auto mb-3 text-gray-200" />
          <p className="text-sm font-semibold text-gray-500">No extension requests yet</p>
          {canCreate && (
            <p className="mt-1 text-xs text-gray-400">
              Use the "Request Extension" button above to submit a deadline extension request.
            </p>
          )}
        </div>
      )}

      {/* Table */}
      {!isLoading && !isError && (extensions ?? []).length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Requested
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Old Deadline
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    New Deadline
                  </th>
                  <th className="px-4 py-3 text-center text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Days
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {(extensions ?? []).map((ext) => (
                  <ExtensionRow
                    key={ext.id}
                    ext={ext}
                    studyRequestId={requestId}
                    canDecide={canDecide}
                    onMutated={() => {
                      qc.invalidateQueries({ queryKey: extKey });
                      onMutated();
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create modal */}
      <RequestExtensionModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(days, reason) => createMutation.mutate({ days, reason })}
        isPending={createMutation.isPending}
        effectiveDate={effectiveDate}
      />
    </div>
  );
}
